// Evaluate a proposed change of an agent's instructions before applying it
// (docs/ai-trace-spec.md §14 C): the agent's recent runs are replayed with the
// proposal (nothing is written to any ticket), a judge rates each replay, the
// reference set is replayed too, and the score decides. Props, file name and
// default export are stable: the ticket window's tests mock this module.
// API: POST /api/ai-assistants/{id}/evaluations, GET …/evaluations/{eid}
// (polled), POST …/evaluations/{eid}/apply[?force=true].
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CircleCheck, FlaskConical, TriangleAlert } from 'lucide-react';
import { Badge, Banner, ConfirmDialog, Field } from '../design/ui';
import { formatPercent } from './aiAgentsShared';
import './EvaluationPanel.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const POLL_MS = 3000;
// French defaults of the existing evaluation.* keys.
const STATUS_DEFAULTS = { queued: 'en attente', running: 'en cours', done: 'terminée', failed: 'échec' };
const ENTRY_DEFAULTS = { ok: 'même chemin', diverged: 'divergent', error: 'erreur', unavailable: 'indisponible' };
const textOf = (error, fallback) => {
  const detail = error && error.response && error.response.data && error.response.data.detail;
  return typeof detail === 'string' ? detail : fallback;
};

// The reference-set gate of an evaluation: the API returns it inside the
// score (golden_pass_rate / golden_gate_passed); `golden` is read when present.
export const goldenGateOf = (evaluation) => {
  if (!evaluation) return null;
  if (evaluation.golden && evaluation.golden.pass_rate !== null && evaluation.golden.pass_rate !== undefined) {
    return { passRate: evaluation.golden.pass_rate, passed: evaluation.golden.gate_passed !== false };
  }
  const score = evaluation.score || {};
  if (score.golden_pass_rate === null || score.golden_pass_rate === undefined) return null;
  return { passRate: score.golden_pass_rate, passed: score.golden_gate_passed !== false, cases: score.golden_cases };
};

const EvaluationPanel = ({ assistantId, initialInstructions = '', approvedModifications = null, sourceRunId = null,
  sourceMessageId = null, comment = null, onApplied, onEvaluated, compact = false }) => {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState(initialInstructions || '');
  const [sampleSize, setSampleSize] = useState(5);
  const [mode, setMode] = useState('call');
  const [evaluation, setEvaluation] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const poll = useCallback(async (id) => {
    try {
      const response = await axios.get(`/api/ai-assistants/${assistantId}/evaluations/${id}`, authHeaders());
      setEvaluation(response.data);
      if (response.data.status === 'queued' || response.data.status === 'running') {
        timerRef.current = setTimeout(() => poll(id), POLL_MS);
      } else if (onEvaluated) {
        onEvaluated(response.data);
      }
    } catch (e) {
      setError(t('evaluation.loadError', 'Impossible de suivre l’évaluation.'));
    }
  }, [assistantId, onEvaluated, t]);

  const launch = async () => {
    setError(null);
    setBusy(true);
    try {
      const body = { sample_size: sampleSize, mode, judge: true, source_run_id: sourceRunId, source_message_id: sourceMessageId, comment };
      if (approvedModifications) body.approved_modifications = approvedModifications;
      else body.instructions = instructions;
      const response = await axios.post(`/api/ai-assistants/${assistantId}/evaluations`, body, authHeaders());
      setEvaluation(response.data);
      if (response.data.status === 'queued' || response.data.status === 'running') {
        timerRef.current = setTimeout(() => poll(response.data.id), POLL_MS);
      }
    } catch (e) {
      setError(textOf(e, t('evaluation.launchError', 'Impossible de lancer l’évaluation.')));
    } finally {
      setBusy(false);
    }
  };

  const golden = goldenGateOf(evaluation);
  const goldenBlocked = Boolean(golden && !golden.passed);

  const apply = async (force = false) => {
    if (!evaluation) return;
    setBusy(true);
    setError(null);
    try {
      const response = await axios.post(`/api/ai-assistants/${assistantId}/evaluations/${evaluation.id}/apply${force ? '?force=true' : ''}`, {}, authHeaders());
      setEvaluation((previous) => ({ ...previous, applied_at: response.data.applied_at, applied_version: response.data.version }));
      setConfirmForce(false);
      if (onApplied) onApplied(response.data, evaluation);
    } catch (e) {
      setConfirmForce(false);
      setError(textOf(e, t('evaluation.applyError', 'Impossible d’appliquer cette version.')));
    } finally {
      setBusy(false);
    }
  };

  const score = evaluation && evaluation.score;
  const pending = Boolean(evaluation && (evaluation.status === 'queued' || evaluation.status === 'running'));
  const verdictGood = Boolean(score)
    && (score.same_decision_rate === null || score.same_decision_rate === undefined || score.same_decision_rate >= 0.8)
    && (score.tool_sequence_match_rate === null || score.tool_sequence_match_rate === undefined || score.tool_sequence_match_rate >= 0.8);

  const scoreTiles = score ? [
    { key: 'decision', value: formatPercent(score.same_decision_rate), label: t('evaluation.sameDecision', 'même décision') },
    { key: 'actions', value: formatPercent(score.same_actions_rate), label: t('evaluation.sameActions', 'mêmes actions') },
    { key: 'tools', value: formatPercent(score.tool_sequence_match_rate), label: t('evaluation.toolsMatch', 'outils identiques') },
    { key: 'drift', value: formatPercent(score.mean_diff_ratio), label: t('evaluation.drift', 'texte modifié') },
    { key: 'replayed', value: `${score.replayed ?? 0}/${score.runs ?? 0}`, label: t('evaluation.replayed', 'rejoués') },
    golden && {
      key: 'golden',
      value: formatPercent(golden.passRate),
      label: golden.cases
        ? t('aiAgentsUi.evaluation.goldenCases', 'jeu de référence ({{count}} cas)', { count: golden.cases })
        : t('evaluation.golden', 'jeu de référence'),
      bad: !golden.passed,
    },
  ].filter(Boolean) : [];

  const entryTone = { ok: 'success', diverged: 'warning', error: 'danger', unavailable: undefined };

  return (
    <div className={`evp-v2${compact ? ' evp-v2--compact' : ''}`}>
      {!evaluation && (
        <div className="u-form">
          {!approvedModifications && (
            <Field label={t('evaluation.instructions', 'Instructions à évaluer')} htmlFor="evp-instructions">
              <textarea
                id="evp-instructions"
                className={`u-textarea evp-v2__textarea${compact ? ' evp-v2__textarea--compact' : ''}`}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
              />
            </Field>
          )}
          <div className="evp-v2__controls">
            <Field label={t('aiAgentsUi.evaluation.sample', 'Traitements rejoués')} htmlFor="evp-sample">
              <select id="evp-sample" className="u-select" value={sampleSize} onChange={(e) => setSampleSize(Number(e.target.value))}>
                {[3, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </Field>
            <Field label={t('evaluation.mode', 'Mode')} htmlFor="evp-mode">
              <select id="evp-mode" className="u-select" value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="call">{t('evaluation.modeCall', 'Appels du modèle (rapide)')}</option>
                <option value="pipeline">{t('evaluation.modePipeline', 'Pipeline complet (plus fidèle)')}</option>
              </select>
            </Field>
            <button
              type="button"
              className="u-btn u-btn--primary evp-v2__launch"
              onClick={launch}
              disabled={busy || (!approvedModifications && !instructions.trim())}
            >
              <FlaskConical aria-hidden="true" />
              {t('evaluation.launch', 'Lancer l’évaluation')}
            </button>
          </div>
          <p className="u-help">{t('aiAgentsUi.evaluation.hint', 'Les derniers traitements de l’agent sont rejoués avec ces instructions. Rien n’est écrit dans les tickets ; aucun outil n’est exécuté.')}</p>
        </div>
      )}

      {error && <Banner tone="danger">{error}</Banner>}

      {evaluation && (
        <div className="evp-v2__result">
          {pending && (
            <Banner tone="info" title={t(`evaluation.status.${evaluation.status}`, STATUS_DEFAULTS[evaluation.status] || evaluation.status)}>
              {t('aiAgentsUi.evaluation.pending', '{{count}} traitements à rejouer. Le résultat s’affiche ici.', { count: (evaluation.sample && evaluation.sample.run_ids && evaluation.sample.run_ids.length) || 0 })}
            </Banner>
          )}
          {evaluation.status === 'failed' && (
            <Banner tone="danger" title={t('evaluation.status.failed', 'échec')}>{evaluation.error}</Banner>
          )}
          {evaluation.status === 'done' && (
            <Banner tone={verdictGood ? 'success' : 'warning'}>
              {verdictGood
                ? t('evaluation.verdictGood', 'La proposition garde les mêmes décisions.')
                : t('evaluation.verdictWarn', 'La proposition change les décisions : à relire avant d’appliquer.')}
            </Banner>
          )}

          {scoreTiles.length > 0 && (
            <div className="evp-v2__scores">
              {scoreTiles.map((tile) => (
                <div key={tile.key} className={`evp-v2__score${tile.bad ? ' is-bad' : ''}`}>
                  <span className="evp-v2__score-value u-num">{tile.value}</span>
                  <span className="evp-v2__score-label">{tile.label}</span>
                </div>
              ))}
            </div>
          )}

          {evaluation.entries && evaluation.entries.length > 0 && (
            <div className="evp-v2__entries">
              {evaluation.entries.map((entry, index) => (
                <details key={entry.source_run_id || index} className="evp-v2__entry">
                  <summary>
                    <Badge tone={entryTone[entry.status]}>{t(`evaluation.entry.${entry.status}`, ENTRY_DEFAULTS[entry.status] || entry.status)}</Badge>
                    {entry.judge && typeof entry.judge.same_decision === 'boolean' && (
                      <Badge tone={entry.judge.same_decision ? 'success' : 'warning'}>
                        {entry.judge.same_decision ? t('evaluation.sameDecision', 'même décision') : t('evaluation.otherDecision', 'décision différente')}
                      </Badge>
                    )}
                    <span className="evp-v2__entry-tools">
                      {(entry.replay_tools || []).map((tool) => tool.name).join(', ') || t('evaluation.noTools', 'aucun outil')}
                    </span>
                    {entry.judge && entry.judge.notes && <span className="evp-v2__entry-notes">{entry.judge.notes}</span>}
                  </summary>
                  <div className="evp-v2__entry-body">
                    {entry.reason && <p className="u-muted">{entry.reason}</p>}
                    {entry.error && <Banner tone="danger">{entry.error}</Banner>}
                    {(entry.source_text || entry.replay_text) && (
                      <div className="evp-v2__compare">
                        <div>
                          <span className="u-overline">{t('evaluation.before', 'Réponse d’origine')}</span>
                          <pre>{entry.source_text}</pre>
                        </div>
                        <div>
                          <span className="u-overline">{t('evaluation.after', 'Réponse rejouée')}</span>
                          <pre>{entry.replay_text}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              ))}
            </div>
          )}

          <div className="evp-v2__actions">
            {evaluation.status === 'done' && !evaluation.applied_at && goldenBlocked && (
              <p className="evp-v2__blocked">
                <TriangleAlert aria-hidden="true" />
                {t('evaluation.goldenBlocked', 'Le jeu de référence de cet assistant échoue avec cette version : l’application est bloquée (vous pouvez forcer).')}
              </p>
            )}
            {evaluation.applied_at && (
              <Badge tone="success" icon={CircleCheck}>
                {t('evaluation.applied', 'Appliquée en version {{version}}', { version: evaluation.applied_version })}
              </Badge>
            )}
            <span className="evp-v2__spacer" />
            {!pending && (
              <button type="button" className="u-btn u-btn--secondary" onClick={() => { setEvaluation(null); setError(null); }} disabled={busy}>
                {t('evaluation.another', 'Autre proposition')}
              </button>
            )}
            {evaluation.status === 'done' && !evaluation.applied_at && (
              goldenBlocked ? (
                <button type="button" className="u-btn u-btn--danger" onClick={() => setConfirmForce(true)} disabled={busy}>
                  {t('evaluation.applyForce', 'Appliquer malgré tout')}
                </button>
              ) : (
                <button type="button" className="u-btn u-btn--primary" onClick={() => apply(false)} disabled={busy}>
                  <CircleCheck aria-hidden="true" />
                  {t('evaluation.apply', 'Appliquer cette version')}
                </button>
              )
            )}
          </div>
        </div>
      )}

      {confirmForce && (
        <ConfirmDialog
          title={t('aiAgentsUi.evaluation.forceTitle', 'Appliquer malgré l’échec du jeu de référence ?')}
          message={t('aiAgentsUi.evaluation.forceMessage', 'Ces instructions deviennent la version de l’agent alors que {{rate}} seulement des cas de référence passent.', { rate: formatPercent(golden && golden.passRate) })}
          confirmLabel={t('evaluation.applyForce', 'Appliquer malgré tout')}
          cancelLabel={t('common.cancel', 'Annuler')}
          busy={busy}
          onCancel={() => !busy && setConfirmForce(false)}
          onConfirm={() => apply(true)}
        />
      )}
    </div>
  );
};

export default EvaluationPanel;
